// Authentication service for Google Workspace integration

import { get, has, isEmpty } from 'lodash'

import { LoginProvider, UserTenant, User } from '@repo/types';
import { api } from '@xtnd/web/shared/api';




export interface AuthState {
  user: User & UserTenant & { picture?: string } | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface SocialLoginUrlResponse {
  loginUrl: string;
  provider: string;
  tenantId: string;
}

export interface SocialLoginParams {
  provider: LoginProvider;
  tenantId?: string;
}

export interface OAuthCallbackParams {
  code: string;
  state: string;
}

export interface OAuthCallbackResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiredAt: number;
  user: {
    id: string;
    email: string;
    name: string;
    picture?: string;
  };
}

class AuthApi {
  private baseResource = '/auth';
  // Get the correct redirect URI for web app
  private getRedirectUri(): string {
    return `${window.location.origin}${this.baseResource}/callback`;
  }


  async getSocialLoginUrl({ provider, tenantId = 'auto' }: SocialLoginParams): Promise<SocialLoginUrlResponse> {
    const redirectUri = this.getRedirectUri();
    const params = new URLSearchParams({
      provider,
      tenantId,
      redirectUri
    });

    if (provider === 'Microsoft') {
      // For Microsoft, construct the backend URL and return it directly
      // The backend will handle the redirect to Microsoft without CORS issues
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const loginUrl = `${baseUrl}${this.baseResource}/microsoft/login`;

      return {
        loginUrl,
        provider: 'Microsoft',
        tenantId: tenantId
      };
    } else {
      return api.get<SocialLoginUrlResponse>(`${this.baseResource}/login-url?${params.toString()}`);
    }
  }

  // Handle OAuth callback
  async handleOAuthCallback({ code, state }: OAuthCallbackParams): Promise<OAuthCallbackResponse> {
    try {
      const response = await api.get<OAuthCallbackResponse>(`${this.baseResource}/callback?code=${code}&state=${state}`);

      // Store tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('idToken', response.idToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('expiredAt', `${response.expiredAt}`);

      const userRes = await api.get<User & UserTenant>(`users/me`);

      localStorage.setItem('user', JSON.stringify(userRes));

      return response;
    } catch (error: any) {
      // Check if this is an unauthorized domain error
      if (error?.response?.status === 401 ||
        (error?.message && error.message.includes('Access denied for domain'))) {

        // Clear any stored tokens
        this.clearAuthData();

        // Redirect to XTND.legal with error message
        const errorMessage = encodeURIComponent(
          error?.message || 'Your domain is not authorized to access this application. Please contact support.'
        );
        window.location.href = `https://xtnd.legal/?error=unauthorized_domain&message=${errorMessage}`;

        // Throw error to prevent further processing
        throw new Error('Unauthorized domain - redirecting to xtnd.legal');
      }

      // Re-throw other errors
      throw error;
    }
  }

  // Handle Microsoft OAuth callback (token already stored, just get user info)
  async handleMicrosoftCallback(): Promise<{ success: boolean }> {
    try {
      // Token should already be stored by the AuthCallback component
      // Just call /auth/me to get enriched user info (tenant alias, name, etc.)
      const userRes = await api.get<User & UserTenant>(`users/me`);
      localStorage.setItem('user', JSON.stringify(userRes));

      return { success: true };
    } catch (error: any) {
      // Clear any stored tokens on error
      this.clearAuthData();
      throw error;
    }
  }

  // Clear authentication data
  private clearAuthData(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiredAt');
    localStorage.removeItem('user');
  }

  // Get current user from localStorage
  getCurrentUser(): User & UserTenant & {
    picture?: string
  } | null {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;

      return JSON.parse(userStr);
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      // Clear tokens and user data
      this.clearAuthData();
    } catch (error) {
      console.error('Sign out failed:', error);
      throw new Error('Failed to sign out');
    }
  }

  isAuthenticated(): boolean {
    // Check if JWT authentication is disabled (for local development)
    if ((import.meta as any).env?.VITE_DISABLE_JWT_AUTH === 'true') {
      return true;
    }

    const accessToken = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');
    return !!(accessToken && user);
  }

  /**
   * Fetches the current authenticated user information from the `/users/me` endpoint.
   *
   * Retrieves detailed user data including tenant information, roles, and user profile.
   * This endpoint requires authentication and will return user data based on the
   * JWT token in the request headers.
   *
   * @returns A promise that resolves to the user data object containing:
   *   - `tenantId`: The tenant identifier
   *   - `userId`: The user's unique identifier
   *   - `email`: User's email address
   *   - `firstName`: User's first name
   *   - `lastName`: User's last name
   *   - `name`: User's full name
   *   - `roles`: Array of user roles
   *   - `isActive`: Whether the user account is active
   *   - `metadata`: Optional additional user metadata
   *   - `createdAt`: Account creation timestamp
   *   - `updatedAt`: Last update timestamp
   *
   * @throws {Error} If the API request fails, the user is not authenticated,
   *   or the user is not found. The error will be logged to the console.
   *
   * @example
   * ```typescript
   * try {
   *   const user = await authApi.getUserMe();
   *   console.log('Current user:', user.email);
   * } catch (error) {
   *   console.error('Failed to fetch user:', error);
   * }
   * ```
   */
  async getUserMe(): Promise<any> {
    try {
      const user = await api.get<User & UserTenant>('users/me');
      return user;
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      throw error;
    }
  }

  /**
   * @typedef {object} User
   * @property {object} roles
   * @property {string[]} [roles.0] - An array of user roles, with the first element being the primary role.
   * * Retrieves the primary role of the current user from local storage.
   * * The user object is expected to be stored in local storage under the key 'user'
   * as a JSON string.
   * * If the 'user' item is not found in local storage, or if the retrieved data
   * is not a valid JSON string or does not contain a role, an empty string is returned.
   * * @returns {string} The primary role of the user, or an empty string if not found.
   */
  getCurrentUserRole() {
    const userString = localStorage.getItem('user');

    if (isEmpty(userString)) {
      return ""
    }

    const user = JSON.parse(userString ?? "");

    // Old data - force sign out to login again
    const hasIsAdmin = has(user, "isAdmin")
    if (hasIsAdmin) {
      return this.signOut();
    }

    return get(user, "roles.0", "")
  }
}

// Export singleton instance
export const authApi = new AuthApi();
